import type {
  PublishOrderLoadDraft,
  PublishOrderLocationDraft,
  PublishOrderPickupRange,
  PublishOrderPriceOverride,
} from '../../usePublishOrderStore';
import type { Currency, VehicleTypes } from '@hero/schema';

/**
 * Inbound shape from the agent's staging payload — what
 * `prepare_create_order_draft` emits on the backend and the
 * `confirmation_required` event ships to the chat store.
 */
export interface AgentLocationPoint {
  lat?: number;
  lng?: number;
  name?: string;
  city?: string;
  savedLocationId?: string;
  isAccurateLocation?: boolean;
  /**
   * Google Maps link this stop came from — pasted by the user or
   * inherited from the saved place. Round-trips through every card
   * edit so the confirmed order keeps the pin the user shared.
   */
  locationUrl?: string;
}

export interface AgentDestinationLane {
  location?: AgentLocationPoint;
  vehicleQuantity?: number;
  price?: {
    itemsOriginalSubtotal?: number;
    currency?: string;
    taxable?: boolean;
  };
  pickupDate?: { from: string; to: string } | string;
}

/** Cargo mention staged by the agent ("مويه 27 طن" → name + weight). */
export interface AgentOrderItem {
  name?: string;
  weight?: { value: number; unit: 'kg' | 'ton' | 'gram' };
  quantity?: number;
}

/** Driver resolved server-side from a name / phone / plate mention. */
export interface AgentOrderDriver {
  driverId?: string;
  vehicleId?: string;
  name?: string;
  phone?: string;
  plate?: string;
}

/**
 * Normalized agent draft — origin shared with N destinations, one
 * destination per route on commit. Matches the shape the
 * `CreateOrderPayloadPreview` UI consumes.
 */
export interface AgentOrderDraft {
  clientId?: string;
  clientName?: string;
  pickupDate?: { from: string; to: string } | string;
  /** Resolved vehicle types — seeded from matched Route or client/org defaults. */
  vehicles?: VehicleTypes[];
  origin: AgentLocationPoint;
  destinations: AgentDestinationLane[];
  /** Cargo staged from the user's message — display + create passthrough. */
  items?: AgentOrderItem[];
  /** Free-text instructions staged from the user's message. */
  notes?: string;
  /** Driver pre-resolved at staging time; assigned on confirm. */
  driver?: AgentOrderDriver;
  /** First-timer affordance: no client attached, offer to link one. */
  noClientHint?: boolean;
}

const readLaneLocation = (
  lane: AgentDestinationLane & Record<string, unknown>,
): AgentLocationPoint | undefined => {
  if (lane.location && typeof lane.location === 'object') {
    return lane.location;
  }
  if (typeof lane.lat === 'number' && typeof lane.lng === 'number') {
    return {
      lat: lane.lat,
      lng: lane.lng,
      name: typeof lane.name === 'string' ? lane.name : undefined,
      city: typeof lane.city === 'string' ? lane.city : undefined,
      savedLocationId:
        typeof lane.savedLocationId === 'string' ? lane.savedLocationId : undefined,
      locationUrl: typeof lane.locationUrl === 'string' ? lane.locationUrl : undefined,
    };
  }
  return undefined;
};

const normalizeDestinationLane = (
  lane: AgentDestinationLane & Record<string, unknown>,
): AgentDestinationLane => ({
  location: readLaneLocation(lane),
  vehicleQuantity: lane.vehicleQuantity ?? 1,
  price: lane.price,
  pickupDate: lane.pickupDate,
});

export const draftLocationToAgentPoint = (
  loc: PublishOrderLocationDraft | null | undefined,
): AgentLocationPoint | undefined => {
  if (!loc) return undefined;
  if (loc.lat !== 0 && loc.lng !== 0) {
    return orderDraftLocationToAgent(loc);
  }
  const name = loc.name?.trim();
  if (name) {
    return { name, city: loc.city ?? name };
  }
  return undefined;
};

const readVehiclesFromPayload = (
  payload: Record<string, unknown>,
): VehicleTypes[] | undefined => {
  const raw = payload.vehicles;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw
    .map(v => (typeof v === 'string' ? v : (v as { type?: string })?.type))
    .filter(Boolean) as VehicleTypes[];
};

/** Serialize scoped draft load fields into agent staging `vehicles[]`. */
export const vehiclesFromOrderDraftLoad = (
  load: PublishOrderLoadDraft,
): VehicleTypes[] | undefined => {
  if (load.vehicleTypes?.length) return load.vehicleTypes;
  if (load.vehicleType) return [load.vehicleType];
  return undefined;
};

const readPickupRange = (
  raw: AgentDestinationLane['pickupDate'] | undefined,
): PublishOrderPickupRange | null => {
  if (raw && typeof raw === 'object' && raw.from && raw.to) {
    return { from: raw.from, to: raw.to };
  }
  if (typeof raw === 'string' && raw.trim()) {
    const from = raw;
    const to = new Date(new Date(from).getTime() + 2 * 60 * 60 * 1000).toISOString();
    return { from, to };
  }
  return null;
};

export const readAgentPickupDate = (
  payload: Record<string, unknown>,
): PublishOrderPickupRange | null =>
  readPickupRange(payload.pickupDate as AgentDestinationLane['pickupDate']);

/**
 * Normalize whatever the LLM emitted (flat / `routes[]` / `origin +
 * destinations[]`) into the canonical `AgentOrderDraft` UI shape.
 */
export const coerceAgentOrderDraft = (
  payload: Record<string, unknown>,
): AgentOrderDraft => {
  const clientId = typeof payload.clientId === 'string' ? payload.clientId : undefined;
  const clientName = String(payload.clientName ?? '').trim() || undefined;
  const pickupDate = payload.pickupDate as AgentDestinationLane['pickupDate'] | undefined;
  const vehicles = readVehiclesFromPayload(payload);
  const items = Array.isArray(payload.items)
    ? (payload.items as AgentOrderItem[])
    : undefined;
  const notes = typeof payload.notes === 'string' ? payload.notes : undefined;
  const driver =
    payload.driver && typeof payload.driver === 'object'
      ? (payload.driver as AgentOrderDriver)
      : undefined;
  const noClientHint = payload.noClientHint === true ? true : undefined;
  const shared = { clientId, clientName, pickupDate, vehicles, items, notes, driver, noClientHint };

  if (
    payload.origin &&
    typeof payload.origin === 'object' &&
    Array.isArray(payload.destinations) &&
    payload.destinations.length > 0
  ) {
    return {
      ...shared,
      origin: payload.origin as AgentLocationPoint,
      destinations: (
        payload.destinations as Array<AgentDestinationLane & Record<string, unknown>>
      ).map(d => normalizeDestinationLane(d)),
    };
  }

  if (Array.isArray(payload.routes) && payload.routes.length > 0) {
    const routes = payload.routes as Array<{
      pickup?: AgentLocationPoint;
      origin?: AgentLocationPoint;
      dropoff?: AgentLocationPoint;
      destination?: AgentLocationPoint;
      vehicleQuantity?: number;
      price?: AgentDestinationLane['price'];
      pickupDate?: AgentDestinationLane['pickupDate'];
    }>;
    const first = routes[0];
    const origin = first.pickup ?? first.origin ?? {};
    return {
      ...shared,
      origin,
      destinations: routes.map(r => ({
        location: r.dropoff ?? r.destination,
        vehicleQuantity: r.vehicleQuantity ?? 1,
        price: r.price,
        pickupDate: r.pickupDate,
      })),
    };
  }

  const pickup = (payload.pickup ?? {}) as AgentLocationPoint;
  const dropoff = (payload.dropoff ?? {}) as AgentLocationPoint;
  return {
    ...shared,
    origin: pickup,
    destinations: [
      {
        location: dropoff,
        vehicleQuantity:
          typeof payload.vehicleQuantity === 'number' ? payload.vehicleQuantity : 1,
        price: payload.price as AgentDestinationLane['price'],
      },
    ],
  };
};

export const agentLocationToOrderDraft = (
  loc: AgentLocationPoint | undefined,
): PublishOrderLocationDraft | null => {
  if (!loc) return null;
  const name = loc.name?.trim() || loc.city?.trim() || '';
  if (loc.lat != null && loc.lng != null) {
    return {
      lat: loc.lat,
      lng: loc.lng,
      name,
      city: loc.city,
      savedLocationId: loc.savedLocationId,
      isAccurate: loc.isAccurateLocation ?? Boolean(loc.savedLocationId),
      locationUrl: loc.locationUrl,
    };
  }
  if (name) {
    return { name, lat: 0, lng: 0, city: loc.city ?? name };
  }
  return null;
};

export const orderDraftLocationToAgent = (
  loc: PublishOrderLocationDraft,
): AgentLocationPoint => ({
  lat: loc.lat,
  lng: loc.lng,
  name: loc.name,
  city: loc.city,
  savedLocationId: loc.savedLocationId,
  isAccurateLocation: loc.isAccurate,
  locationUrl: loc.locationUrl,
});

const toPriceOverride = (
  price: AgentDestinationLane['price'],
): PublishOrderPriceOverride | undefined => {
  if (price?.itemsOriginalSubtotal == null) return undefined;
  return {
    itemsOriginalSubtotal: price.itemsOriginalSubtotal,
    currency: (price.currency as Currency) ?? 'SAR',
    taxable: price.taxable ?? true,
  };
};

/**
 * Hydrate an `OrderDraftState`-shaped slice from a normalized agent
 * draft. The fields it doesn't fill (load, allocation, instructions,
 * etc.) keep their store defaults so the publish-style UI keeps
 * working for code paths that read those fields.
 */
export const fromAgentOrderDraft = (
  draft: AgentOrderDraft,
): {
  customer: { customerId?: string; name: string } | null;
  origin: PublishOrderLocationDraft | null;
  destinations: PublishOrderLocationDraft[];
  destinationVehicleQuantities: number[];
  pickupDate: PublishOrderPickupRange | null;
  priceOverrides: Array<PublishOrderPriceOverride | undefined>;
  load: Partial<PublishOrderLoadDraft>;
} => {
  const origin = agentLocationToOrderDraft(draft.origin);
  const destinations: PublishOrderLocationDraft[] = [];
  const destinationVehicleQuantities: number[] = [];
  const priceOverrides: Array<PublishOrderPriceOverride | undefined> = [];
  for (const dest of draft.destinations) {
    destinations.push(
      agentLocationToOrderDraft(readLaneLocation(dest as AgentDestinationLane & Record<string, unknown>)) ?? {
        name: '',
        lat: 0,
        lng: 0,
      },
    );
    destinationVehicleQuantities.push(dest.vehicleQuantity ?? 1);
    priceOverrides.push(toPriceOverride(dest.price));
  }
  const load: Partial<PublishOrderLoadDraft> = draft.vehicles?.length
    ? {
        vehicleTypes: draft.vehicles,
        vehicleType: draft.vehicles[0],
      }
    : {};

  return {
    customer:
      draft.clientId || draft.clientName
        ? {
            customerId: draft.clientId,
            name: draft.clientName ?? '',
          }
        : null,
    origin,
    destinations,
    destinationVehicleQuantities,
    pickupDate: readPickupRange(draft.pickupDate),
    priceOverrides,
    load,
  };
};

/**
 * Build the agent staging JSON shape that the backend's
 * `mapAgentOrderPayload` accepts on confirm. Mirrors
 * `fromAgentOrderDraft` so a round-trip (agent → state → agent) is
 * lossless for everything the user can edit in the confirmation
 * card.
 */
export const toAgentStagingPayload = (input: {
  clientId?: string;
  clientName?: string;
  pickupDate?: PublishOrderPickupRange | null;
  vehicles?: VehicleTypes[];
  origin: AgentLocationPoint;
  destinations: AgentDestinationLane[];
  /**
   * Staging fields the card doesn't edit (cargo, notes, resolved
   * driver, first-timer hint) — MUST round-trip through every edit or
   * the confirm payload silently loses them.
   */
  items?: AgentOrderItem[];
  notes?: string;
  driver?: AgentOrderDriver;
  noClientHint?: boolean;
}): Record<string, unknown> => ({
  clientId: input.clientId,
  clientName: input.clientName,
  pickupDate: input.pickupDate
    ? { from: input.pickupDate.from, to: input.pickupDate.to }
    : undefined,
  ...(input.vehicles?.length ? { vehicles: input.vehicles } : {}),
  ...(input.items?.length ? { items: input.items } : {}),
  ...(input.notes?.trim() ? { notes: input.notes } : {}),
  ...(input.driver ? { driver: input.driver } : {}),
  ...(input.noClientHint ? { noClientHint: true } : {}),
  origin: input.origin,
  destinations: input.destinations.map(d => ({
    location: d.location,
    vehicleQuantity: d.vehicleQuantity ?? 1,
    price: d.price,
    pickupDate: d.pickupDate,
  })),
});

export const locationLabel = (
  loc: AgentLocationPoint | undefined,
  fallback: string,
): string => loc?.name?.trim() || loc?.city?.trim() || fallback;
